/**
 * น้ำหนักของการโจมตี (hit weight): how hard a landed blow shoves and staggers its target, by the move that landed it
 * and how it came out. One pure lookup (`hitWeightOf`) read by both sides:
 * - the rules (systems/hitWeight.ts): the knock-back step and the stagger's short stun on the monsters that take them
 *   (`HIT_WEIGHT.monsters`);
 * - the client: the stagger reaction, the hit-stop and the camera kick, from the `hit` event alone.
 *
 * Light blows (the basic chain, weak moves) barely rock the target; strong moves and a parried blow push it a step;
 * a break (破) that lands, a won interrupt and a power move (the 斬 finisher) throw it back and stagger it.
 */
import type { HitOutcome } from '../combat';
import { SKILLS } from './skills';

export type HitWeightClass = 'guard' | 'light' | 'medium' | 'heavy';

export interface HitWeight {
  readonly cls: HitWeightClass;
  /** Knock-back distance (m), eased out over `HIT_WEIGHT.knockTime`. */
  readonly knock: number;
  /** How long the target reels (s): the drawn reaction; a heavy one also stuns for `stun`. */
  readonly stagger: number;
  /** Stun the stagger lays on (s, at least; a longer stun already taken stands). 0: the target keeps acting. */
  readonly stun: number;
  /** The client's hit-stop: the whole scene held this long (s). */
  readonly hitStop: number;
  /** The client's camera kick (m along the blow). */
  readonly kick: number;
}

export const HIT_WEIGHT = {
  /** The knock-back plays out over this long (s): a velocity easing out, ×e^(−k t). */
  knockTime: 0.28,
  /** Moves at or above this power are strong (medium) when they land unguarded. */
  strongPower: 1.5,
  /** The monsters the rules shove and stagger (data/monsters.ts ids): the yard's sparring pupils for now. */
  monsters: ['sparrer', 'sparrer-yard'] as readonly string[],
  classes: {
    guard: { cls: 'guard', knock: 0.14, stagger: 0.16, stun: 0, hitStop: 0, kick: 0 },
    light: { cls: 'light', knock: 0.12, stagger: 0.22, stun: 0, hitStop: 0, kick: 0 },
    medium: { cls: 'medium', knock: 0.38, stagger: 0.36, stun: 0, hitStop: 0.045, kick: 0.05 },
    heavy: { cls: 'heavy', knock: 1.1, stagger: 0.72, stun: 0.35, hitStop: 0.11, kick: 0.16 },
  } as Readonly<Record<HitWeightClass, HitWeight>>,
} as const;

/** The weight of a blow by its move (`basic` for the chain) and outcome; null when it did not connect (a dodge). */
export function hitWeightOf(skillId: string, outcome: HitOutcome): HitWeight | null {
  const C = HIT_WEIGHT.classes;
  if (outcome === 'dodge') return null;
  if (outcome === 'blocked') return C.guard;
  if (outcome === 'guardBreak' || outcome === 'interrupt') return C.heavy;
  const s = SKILLS[skillId];
  if (!s) return C.light;
  if (s.kind === 'power' || s.kind === 'break') return C.heavy;
  if (s.kind === 'attack' && s.power >= HIT_WEIGHT.strongPower) return C.medium;
  return C.light;
}
